import React, { useEffect, useRef, useState } from "react";
import "./Companies.css";
import { Button } from "@mui/material";
import { AddCompany } from "./AddCompany";
import ProgressBar from "../../../UI/ProgressBar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetAllCompaniesQuery,
  useDeleteCompanyMutation,
} from "../../../../redux-toolkit/features/company/companyApiSlice";
import CompanyDrawer from "./CompanyDrawer";
import { useNavigate } from "react-router-dom";
import { DeleteModal } from "../../../UI/Delete Modal/DeleteModal";
import { useAlert } from "../../../../hooks/useAlert";
import { Error404 } from "../../../UI/404/Error404";
import { useOutsideClick } from "../../../../hooks/useOutsideClick";
import { Search } from "../../../UI/Search/Search";
import defaultPreviewLogo from "../../../../assets/company/logo_default_preview.svg";
import { useSelector } from "react-redux";

export const Companies = () => {
  const [companyOpen, setCompanyOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef(null);
  const { data, isLoading, isError } = useGetAllCompaniesQuery(searchQuery, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteCompany, { isLoading: isDeleting }] = useDeleteCompanyMutation();
  const [drawer, setDrawer] = React.useState(0);
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { displayAlert } = useAlert();
  const [companyId, setCompanyId] = useState("");
  const [companyAdmin, setCompanyAdmin] = React.useState();
  const [name, setName] = React.useState();
  const role = useSelector((state) => state.auth.user?.role);
  const allowedAccess =
    useSelector((state) => state.auth.user?.allowedAccess) || [];
  const canDeleteCompany =
    role === "SUPERADMIN" ||
    (role === "CUSTOM_ROLE" && allowedAccess.includes("SUPERADMIN_COMPANY"));

  const handleOpen = () => {
    setCompanyOpen(true);
  };
  useOutsideClick(ref, () => {
    setDrawer(0);
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const viewBooksHandler = (id) => {
    navigate(`/super-admin/manage-organization/companies/books/${id}`);
  };

  if (isLoading) {
    return <ProgressBar />;
  }
  if (isError) {
    return <Error404 />;
  }

  const deleteDepHandler = async (note) => {
    if (!canDeleteCompany) {
      displayAlert({
        open: true,
        message: "You do not have permission to delete companies",
        type: "error",
        timeout: "2000",
      });
      return;
    }
    if (!companyId) return;
    try {
      const res = await deleteCompany({
        companyId,
        note: note || "",
      }).unwrap();
      displayAlert({
        open: true,
        message: res?.message || "Company Deleted Successfully!",
        type: "success",
        timeout: "2000",
      });
      setCompanyId("");
      setName("");
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || "Error while deleting the company",
        type: "error",
        timeout: "2000",
      });
    }
  };

  const editDepHandler = () => {
    handleOpen();
  };

  return (
    <div className="container">
      <div className="companies__actions">
        <div className="companies__search">
          <Search
            placehold="Search companies..."
            searchVal={searchInput}
            setSearchVal={setSearchInput}
          />
        </div>
        <Button
          variant="contained"
          onClick={() => {
            handleOpen();
            setName("");
            setCompanyId("");
          }}
        >
          Add Company
        </Button>
      </div>
      <AddCompany
        setDrawer={setDrawer}
        companyOpen={companyOpen}
        setCompanyOpen={setCompanyOpen}
        companyId={companyId}
        setName={setName}
        companyAdmin={companyAdmin}
        name={name}
        setCompanyAdmin={setCompanyAdmin}
      />
      <DeleteModal
        label={name || "Company"}
        deleteType="company"
        deleteHandler={deleteDepHandler}
        setDeleteOpen={setDeleteOpen}
        deleteOpen={canDeleteCompany && deleteOpen}
        cascadeWarning="All users belonging to this company will also be deleted."
        permanentWarning="This action is permanent and cannot be undone."
        noteEnabled
        noteLabel="Deletion reason (optional)"
        notePlaceholder="Add a note about why this company is being deleted"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDeleting={isDeleting}
      />
      <div className="companies">
        {data &&
          data?.data.companies.map((elm, index) => (
            <div key={index} className="companies__cards">
              <div className="companies__more">
                {drawer != elm._id && (
                  <MoreVertIcon
                    onClick={() => setDrawer(elm._id)}
                    sx={{
                      color: "white",
                      fontSize: "20px",
                      margin: "5px",
                      padding: "2px",
                      background: "rgba(68, 68, 68, 0.4)",
                      borderRadius: "5px",
                    }}
                  />
                )}
                {drawer === elm._id && (
                  <div ref={ref}>
                    <CompanyDrawer
                      editDepHandler={editDepHandler}
                      index={elm._id}
                      setDrawer={setDrawer}
                      setDeleteOpen={setDeleteOpen}
                      setCompanyId={setCompanyId}
                      setCompanyName={setName}
                      companyName={elm.name}
                      canDelete={canDeleteCompany && elm.isMutable !== false}
                    />
                  </div>
                )}
              </div>
              {elm.coverImage ? (
                <img
                  onClick={() => setDrawer(0)}
                  src={elm.coverImage}
                  alt=""
                />
              ) : (
                <div
                  className="companies__defaultCover"
                  onClick={() => setDrawer(0)}
                >
                  <img src={defaultPreviewLogo} alt="" />
                </div>
              )}
              <h6 style={{ fontSize: "14px", fontWeight: "500" }}>
                {elm.name}
              </h6>
              <div className="companies__cardsActions">
                <button
                  onClick={() => viewBooksHandler(elm._id)}
                  className="contained__deplgbtn"
                >
                  View All Books
                </button>
              </div>
            </div>
          ))}
        <div
          onClick={() => {
            handleOpen();
            setName("");
            setCompanyId("");
          }}
          className="companies__cards"
        >
          <div className="companies__addCompany">
            <AddRoundedIcon
              sx={{ fontSize: "70px", color: "var(--grey400)" }}
            />
          </div>
          <h6 style={{ fontSize: "14px", fontWeight: "500" }}>Add Company</h6>
          <div className="companies__addCompanyh6"></div>
        </div>
      </div>
    </div>
  );
};
